if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer') && !this.skipCart) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Back from checkout via the back/forward cache: the page comes back
        // exactly as it was left — mid-redirect, spinner on, button disabled.
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) this.resetSubmitState();
        });
      }

      // "Skip cart, go straight to checkout" on the buy_buttons block.
      get skipCart() {
        return this.dataset.skipCart === 'true' || this.form?.dataset.skipCart === 'true';
      }

      resetSubmitState() {
        this.redirecting = false;
        this.error = false;
        this.submitButton.classList.remove('loading', 'atc-locked');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      goToCheckout() {
        this.redirecting = true;
        if (window.VrnSkipCheckout) {
          window.VrnSkipCheckout.go();
          return;
        }
        if (typeof window.vrnArmCheckoutReturn === 'function') window.vrnArmCheckoutReturn();
        window.location.assign('/checkout');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // A cart clear started by "clear cart when returning from checkout" can
        // still be in flight if the shopper presses straight away. Adding before
        // it lands would let the clear wipe the new item.
        Promise.resolve(window.__vrnReturnClear)
          .catch(() => {})
          .then(() => this.submitToCart(evt));
      }

      submitToCart(evt) {
        const skipCart = this.skipCart;
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        // return_to is only the no-JavaScript fallback for skip-to-checkout. On
        // this AJAX request it would turn the JSON response into a redirect.
        formData.delete('return_to');
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const pendingUpsellItems = this.getPendingUpsellItems();
        const sections = this.cart && !skipCart
          ? this.cart.getSectionsToRender().map((section) => section.id)
          : [];

        if (this.cart && !skipCart) {
          this.cart.setActiveElement(document.activeElement);
        }

        let cartItems = [{ id: Number(variantId), quantity }];
        if (pendingUpsellItems.length) {
          const mainItem = this.buildMainLineItem(formData, quantity);
          cartItems = [mainItem, ...pendingUpsellItems];
          config.headers['Content-Type'] = 'application/json';
          config.headers.Accept = 'application/json';
          config.body = JSON.stringify({
            items: cartItems,
            ...(sections.length ? { sections } : {}),
            sections_url: window.location.pathname,
          });
        } else {
          if (sections.length) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        const cartLines = cartItems.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        }));
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(cartLines);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));
              if (skipCart) this.cancelCheckoutIntent();

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (skipCart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              this.goToCheckout();
              // Subscribers (upsell state, analytics hooks) still hear about the
              // add; a throwing subscriber must not cancel the redirect above.
              try {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: variantId,
                  cartData: response,
                }).catch(() => {});
              } catch (e) {}
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
            if (skipCart) {
              this.cancelCheckoutIntent();
              this.handleErrorMessage(window.cartStrings?.error || 'Something went wrong. Please try again.');
            }
          })
          .finally(() => {
            // Leaving for checkout: keep the button busy until the page unloads,
            // so a second press cannot start a second add.
            if (this.redirecting) {
              CartPerformance.measureFromEvent("add:user-action", evt);
              return;
            }
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      cancelCheckoutIntent() {
        window.VrnSkipCheckout?.cancel();
        if (typeof window.vrnDisarmCheckoutReturn === 'function') window.vrnDisarmCheckoutReturn();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            this.submitButton.dataset.atcLabel || window.variantStrings.addToCart;
        }
      }

      getPendingUpsellItems() {
        const scope = this.closest('product-info');
        if (!scope) return [];
        const seenVariants = new Set();

        return Array.from(
          scope.querySelectorAll('product-upsell .product-upsell-toggle[data-state="pending"]')
        ).reduce((items, button) => {
          const variantId = Number(button.dataset.variantId);
          if (!variantId || seenVariants.has(variantId)) return items;

          seenVariants.add(variantId);
          items.push({ id: variantId, quantity: 1 });
          return items;
        }, []);
      }

      buildMainLineItem(formData, quantity) {
        const item = {
          id: Number(formData.get('id')),
          quantity,
        };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = Number(sellingPlan) || sellingPlan;

        const properties = {};
        formData.forEach((value, key) => {
          const property = key.match(/^properties\[(.*)\]$/);
          if (!property || value === '') return;
          properties[property[1]] = typeof value === 'string' ? value : value.name;
        });
        if (Object.keys(properties).length) item.properties = properties;

        return item;
      }

      createCartLinesUpdateEvent(lines) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent || !lines.length) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines,
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
